/**
 * Folder upload — collect an entire directory tree (sub-directories included)
 * and carry each file's path *relative to the picked folder* through to the
 * upload request.
 *
 * Transport: the backend reads the sub-path off the multipart `filename` part
 * (`upload_files_to_storage` → `original_filename` → `FileAttachment.relative_path`).
 * There is no separate form field for it, so `uploadEngine` sends the relative
 * path as the part filename (`formData.append("files", file, relPath)`).
 *
 * Because `File.webkitRelativePath` is read-only and absent on drag-dropped
 * entries, the resolved path is kept in a module-level `WeakMap` keyed by the
 * `File` object rather than by mutating or re-wrapping it.
 *
 * The top-level directory name is stripped: the PROJECT NAME the user types is
 * the root of the deal room, not the folder name on their laptop.
 */

/** Resolved relative paths, keyed by the `File` they belong to. */
const relativePaths = new WeakMap<File, string>();

export const setRelativePath = (file: File, relativePath: string): void => {
	relativePaths.set(file, relativePath);
};

/**
 * The path a file should be stored under, relative to the project root.
 * Falls back to `webkitRelativePath` (directory picker) and then the bare
 * filename, so plain single-file uploads are unaffected.
 */
export const relativePathFor = (file: File): string => {
	const tracked = relativePaths.get(file);
	if (tracked) return tracked;
	const webkitPath = normalizeRelativePath(file.webkitRelativePath || "");
	return webkitPath || file.name;
};

/**
 * Normalize a browser-supplied path: forward slashes, no leading slash, no
 * `.`/`..` segments. Traversal segments cannot escape anything here (the GCS
 * blob name is a UUID) but they must not reach the stored `relative_path`.
 */
export const normalizeRelativePath = (path: string): string =>
	path
		.replace(/\\/g, "/")
		.split("/")
		.filter((segment) => segment !== "" && segment !== "." && segment !== "..")
		.join("/");

/**
 * Drop the first path segment — the local folder's own name. `webkitRelativePath`
 * is always rooted at the picked directory ("Birchwood/Legal/loan.pdf"), and the
 * project name supplies that root instead ("Legal/loan.pdf").
 * A path with no sub-directory is returned unchanged.
 */
export const stripTopLevelSegment = (path: string): string => {
	const segments = normalizeRelativePath(path).split("/");
	return segments.length > 1 ? segments.slice(1).join("/") : segments.join("/");
};

/**
 * The single root segment shared by every path, or `null` when they do not all
 * share one.
 *
 * Only a common root may be stripped. A multi-folder drop ("A/x.pdf" + "B/x.pdf")
 * or a folder mixed with loose files has no single local root standing in for
 * the project name, and stripping the first segment there would silently merge
 * distinct trees — turning two different files into the same relative path.
 */
export const commonRootSegment = (paths: string[]): string | null => {
	let root: string | null = null;
	for (const path of paths) {
		const segments = normalizeRelativePath(path).split("/");
		// A loose file at the top level has no root to share.
		if (segments.length < 2) return null;
		if (root === null) root = segments[0];
		else if (root !== segments[0]) return null;
	}
	return root;
};

/** The picked folder's own name, i.e. the segment `stripTopLevelSegment` drops. */
export const topLevelSegment = (path: string): string => {
	const segments = normalizeRelativePath(path).split("/");
	return segments.length > 1 ? segments[0] : "";
};

/** OS/tooling debris that must never become a deal-room document. */
const JUNK_BASENAMES = new Set([
	".ds_store",
	"thumbs.db",
	"desktop.ini",
	".localized",
	"icon\r",
]);

/**
 * True for files that should be silently dropped from a folder upload:
 * OS metadata, AppleDouble resource forks, `__MACOSX` shadow trees, and
 * zero-byte hidden files.
 */
export const isJunkFile = (relativePath: string, size: number): boolean => {
	const segments = normalizeRelativePath(relativePath).split("/");
	if (segments.some((segment) => segment === "__MACOSX")) return true;

	const basename = segments[segments.length - 1] ?? "";
	if (JUNK_BASENAMES.has(basename.toLowerCase())) return true;
	if (basename.startsWith("._")) return true;
	if (basename.startsWith(".") && size === 0) return true;
	return false;
};

export interface CollectedFolder {
	/** Files to upload, each registered in the relative-path map. */
	files: File[];
	/** Name of the picked folder, for the count preview. */
	folderName: string;
	/** Junk files dropped (.DS_Store & friends). */
	skippedJunk: number;
	/** Files dropped because the deal room does not accept the type. */
	skippedUnsupported: number;
	/**
	 * Entries the browser refused to read, with their paths. Non-empty means the
	 * selection is INCOMPLETE — the UI must surface this and make the user
	 * acknowledge it before uploading.
	 */
	failed: FailedEntry[];
	/**
	 * Distinct relative paths claimed by more than one file. Non-empty means the
	 * selection cannot be uploaded as-is: two files would collide on one path.
	 */
	collisions: PathCollision[];
	/**
	 * Files the server would reject with a 422. Caught here, before the first
	 * request, so a folder never uploads part of itself and then fails.
	 */
	unstorable: UnstorablePath[];
}

export interface UnstorablePath {
	relativePath: string;
	problems: string[];
}

export interface FailedEntry {
	path: string;
	reason: string;
}

/**
 * Mirror of the server's limits (`upload_utils.MAX_FILENAME_LENGTH` /
 * `MAX_RELATIVE_PATH_LENGTH`, themselves the `file_attachments` column widths).
 * Kept in sync deliberately: the point of the preflight is that the client
 * rejects exactly what the server would, so a folder never uploads half of
 * itself and then 422s.
 */
export const MAX_FILENAME_LENGTH = 255;
export const MAX_RELATIVE_PATH_LENGTH = 1000;

/**
 * Every reason a path would be rejected by the server, checked client-side
 * before the first request. Same rules, same order as `upload_utils.path_problems`.
 */
export const pathProblems = (rawPath: string): string[] => {
	const problems: string[] = [];
	const normalized = rawPath.replace(/\\/g, "/");
	const relativePath = normalizeRelativePath(rawPath);
	const basename = relativePath.split("/").pop() ?? "";

	if (normalized.endsWith("/")) return ["is a directory, not a file"];
	if (!basename) return ["resolves to an empty filename"];
	// eslint-disable-next-line no-control-regex
	if (/[\u0000-\u001F\u007F]/.test(rawPath)) {
		problems.push("contains control characters");
	}
	if (basename.length > MAX_FILENAME_LENGTH) {
		problems.push(
			`filename is ${basename.length} characters (limit ${MAX_FILENAME_LENGTH})`
		);
	}
	if (relativePath.length > MAX_RELATIVE_PATH_LENGTH) {
		problems.push(
			`path is ${relativePath.length} characters (limit ${MAX_RELATIVE_PATH_LENGTH})`
		);
	}
	return problems;
};

export interface PathCollision {
	relativePath: string;
	names: string[];
}

/**
 * Turn a raw directory selection into an upload-ready list.
 *
 * `pathFor` resolves each file's path *including* the top-level folder name —
 * `webkitRelativePath` for the directory picker, the traversed `fullPath` for a
 * drag-dropped folder. `isSupported` mirrors the picker's own type gate so
 * folder uploads reject exactly what file uploads reject.
 */
export const collectFolderFiles = (
	files: File[],
	isSupported: (file: File) => boolean,
	pathFor: (file: File) => string = (file) =>
		file.webkitRelativePath || file.name,
	failed: FailedEntry[] = []
): CollectedFolder => {
	const kept: Array<{ file: File; fullPath: string }> = [];
	let skippedJunk = 0;
	let skippedUnsupported = 0;

	for (const file of files) {
		const fullPath = normalizeRelativePath(pathFor(file));

		if (isJunkFile(fullPath, file.size)) {
			skippedJunk += 1;
			continue;
		}
		if (!isSupported(file)) {
			skippedUnsupported += 1;
			continue;
		}
		kept.push({ file, fullPath });
	}

	// Strip the local folder name only when every kept file actually shares it;
	// otherwise the paths stay as-is and the project name simply sits above them.
	const root = commonRootSegment(kept.map((entry) => entry.fullPath));
	const resolve = (fullPath: string) =>
		root === null ? fullPath : stripTopLevelSegment(fullPath);

	const byPath = new Map<string, File[]>();
	const unstorable: UnstorablePath[] = [];
	for (const { file, fullPath } of kept) {
		const relativePath = resolve(fullPath);
		setRelativePath(file, relativePath);

		// Preflight against the server's own rules, before anything is sent.
		const problems = pathProblems(relativePath);
		if (problems.length > 0) unstorable.push({ relativePath, problems });

		const existing = byPath.get(relativePath);
		if (existing) existing.push(file);
		else byPath.set(relativePath, [file]);
	}

	const collisions: PathCollision[] = [];
	for (const [relativePath, claimants] of byPath) {
		if (claimants.length > 1) {
			collisions.push({
				relativePath,
				names: claimants.map((file) => file.name),
			});
		}
	}

	return {
		files: kept.map((entry) => entry.file),
		folderName: root ?? "",
		skippedJunk,
		skippedUnsupported,
		failed,
		collisions,
		unstorable,
	};
};

/**
 * Minimal shape of the non-standard `FileSystemEntry` API used for dropped
 * folders. Typed locally because `lib.dom` models it as `any`-ish and only
 * Chromium/WebKit implement `webkitGetAsEntry`.
 */
interface DroppedEntry {
	isFile: boolean;
	isDirectory: boolean;
	fullPath: string;
	file?: (onSuccess: (file: File) => void, onError: (err: unknown) => void) => void;
	createReader?: () => {
		readEntries: (
			onSuccess: (entries: DroppedEntry[]) => void,
			onError: (err: unknown) => void
		) => void;
	};
}

/**
 * Traversal bounds. The entry API has no cycle detection, and a symlinked or
 * pathological tree can recurse or fan out without end — these turn that into a
 * reported failure rather than a hung tab.
 */
export const MAX_TRAVERSAL_DEPTH = 32;
export const MAX_TRAVERSAL_ENTRIES = 20000;

/**
 * Drain a directory reader, which returns at most ~100 entries per call.
 *
 * `budget()` is consulted between calls and every entry counts against it —
 * directories included. Enumeration STOPS requesting more as soon as the budget
 * is gone, so a pathologically wide directory is never fully accumulated in
 * memory first.
 */
const readAllEntries = (
	reader: NonNullable<ReturnType<NonNullable<DroppedEntry["createReader"]>>>,
	onError: (reason: string) => void,
	/** Reserves up to `count` units of budget; returns how many were granted. */
	reserve: (count: number) => number,
	onBudgetExhausted: () => void
): Promise<DroppedEntry[]> =>
	new Promise((resolve) => {
		const all: DroppedEntry[] = [];
		const readBatch = () => {
			reader.readEntries(
				(batch) => {
					if (batch.length === 0) {
						resolve(all);
						return;
					}
					// Charge the budget as entries stream in — directories included —
					// and never keep more than it allows, even within one page.
					const granted = reserve(batch.length);
					all.push(...batch.slice(0, granted));
					if (granted < batch.length) {
						// Truncated: say so, or a partial directory would look like a
						// complete one. Stop asking for more pages.
						onBudgetExhausted();
						resolve(all);
						return;
					}
					readBatch();
				},
				(err) => {
					// Resolve with what was read AND report it: a partial directory
					// must not look like a complete one.
					onError(errorReason(err));
					resolve(all);
				}
			);
		};
		readBatch();
	});

const errorReason = (err: unknown): string => {
	if (err && typeof err === "object" && "name" in err) {
		return String((err as { name: unknown }).name);
	}
	return err ? String(err) : "unreadable";
};

interface WalkState {
	out: Array<{ file: File; fullPath: string }>;
	failed: FailedEntry[];
	seen: number;
	stopped: boolean;
}

/** Record that enumeration hit the ceiling, once. */
const stopForBudget = (state: WalkState, path: string): void => {
	if (state.stopped) return;
	state.stopped = true;
	state.failed.push({
		path,
		reason: `stopped after ${MAX_TRAVERSAL_ENTRIES} entries — selection is incomplete`,
	});
};

const walkEntry = async (
	entry: DroppedEntry,
	state: WalkState,
	depth: number
): Promise<void> => {
	// `fullPath` is rooted at the drop ("/Birchwood/Legal/loan.pdf").
	const path = normalizeRelativePath(entry.fullPath) || "(unnamed)";

	// Budget was already charged when this entry was ENUMERATED, so everything
	// that got here is within it and is worth reading. Entries beyond the
	// ceiling were never enumerated in the first place.
	if (depth > MAX_TRAVERSAL_DEPTH) {
		state.failed.push({
			path,
			reason: `deeper than ${MAX_TRAVERSAL_DEPTH} levels — not read`,
		});
		return;
	}

	if (entry.isFile && entry.file) {
		const file = await new Promise<File | null>((resolve) => {
			try {
				entry.file!(resolve, () => resolve(null));
			} catch {
				resolve(null);
			}
		});
		if (file) state.out.push({ file, fullPath: path });
		else state.failed.push({ path, reason: "could not be read" });
		return;
	}

	if (entry.isDirectory && entry.createReader) {
		let reader;
		try {
			reader = entry.createReader();
		} catch (err) {
			state.failed.push({ path, reason: errorReason(err) });
			return;
		}
		const children = await readAllEntries(
			reader,
			(reason) => state.failed.push({ path, reason }),
			(count) => {
				const granted = Math.max(
					0,
					Math.min(count, MAX_TRAVERSAL_ENTRIES - state.seen)
				);
				state.seen += granted;
				return granted;
			},
			() => stopForBudget(state, path)
		);
		for (const child of children) {
			await walkEntry(child, state, depth + 1);
		}
	}
};

export interface DroppedTraversal {
	entries: Array<{ file: File; fullPath: string }>;
	failed: FailedEntry[];
}

/**
 * Recursively read a drag-dropped folder. Returns `null` when the drop contains
 * no directory (or the browser lacks `webkitGetAsEntry`), so the caller can fall
 * back to the plain `dataTransfer.files` path.
 *
 * Entries the browser refuses to read are reported in `failed` rather than
 * silently dropped — a partial folder must never be presented as a whole one.
 */
export const collectDroppedEntries = async (
	dataTransfer: DataTransfer
): Promise<DroppedTraversal | null> => {
	const items = Array.from(dataTransfer.items || []);
	const resolved = items.map((item) =>
		typeof item.webkitGetAsEntry === "function"
			? (item.webkitGetAsEntry() as DroppedEntry | null)
			: null
	);
	const entries = resolved.filter(
		(entry): entry is DroppedEntry => entry !== null
	);

	if (entries.length === 0 || !entries.some((entry) => entry.isDirectory)) {
		return null;
	}

	const state: WalkState = {
		out: [],
		failed: [],
		// The dropped roots are themselves enumerated entries.
		seen: entries.length,
		stopped: false,
	};

	// A root the browser would not resolve to an entry was previously dropped
	// on the floor. Once we KNOW this drop is a directory traversal, a
	// null root is a hole in the selection, not a non-event — report it so the
	// acknowledgement gate applies rather than uploading a quietly smaller set.
	const unresolvedRoots = resolved.length - entries.length;
	for (let i = 0; i < unresolvedRoots; i += 1) {
		state.failed.push({
			path: `(dropped item ${i + 1})`,
			reason: "the browser would not open it — not included",
		});
	}

	for (const entry of entries) {
		await walkEntry(entry, state, 0);
	}
	return { entries: state.out, failed: state.failed };
};
